import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";
import { User, UserFormData } from "./user";

class UserService {
  // Get all users with pagination and filtering
  async getUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    status?: string;
    search?: string;
    branchId?: string;
  }): Promise<ApiResponse<{ users: User[]; pagination: any }>> {
    const response = await enhancedApiClient.get("/users", { 
      params,
      context: "Fetch Users",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get all users for role assignment (no pagination)
  async getAllUsers(): Promise<ApiResponse<User[]>> {
    const response = await enhancedApiClient.get("/users/all", {
      context: "Fetch All Users",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get user by ID
  async getUser(userId: string): Promise<ApiResponse<User>> {
    console.log('UserService - Getting user with ID:', userId);
    const response = await enhancedApiClient.get(`/users/${userId}`, {
      context: "Fetch User By ID",
      component: "UserService"
    });
    
    console.log('UserService - API Response:', response);
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Create new user
  async createUser(
    userData: UserFormData & { password: string }
  ): Promise<ApiResponse<User>> {
    console.log("🌐 API Service - Creating user with data:", userData);
    console.log("🏢 API Service - Branch ID:", userData.branchId);
    
    const response = await enhancedApiClient.post("/users", userData, {
      context: "Create User",
      component: "UserService"
    });
    
    console.log("📡 API Service - Response received:", response);
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Update user
  async updateUser(
    userId: string,
    userData: Partial<UserFormData>
  ): Promise<ApiResponse<User>> {
    const response = await enhancedApiClient.put(`/users/${userId}`, userData, {
      context: "Update User",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Delete user
  async deleteUser(userId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await enhancedApiClient.delete(`/users/${userId}`, {
      context: "Delete User",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get users by role
  async getUsersByRole(role: string): Promise<ApiResponse<User[]>> {
    const response = await enhancedApiClient.get(`/users?role=${role}`, {
      context: "Get Users By Role",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get users by status
  async getUsersByStatus(status: string): Promise<ApiResponse<User[]>> {
    const response = await enhancedApiClient.get(`/users?status=${status}`, {
      context: "Get Users By Status",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Search users
  async searchUsers(searchTerm: string): Promise<ApiResponse<User[]>> {
    const response = await enhancedApiClient.get(`/users?search=${searchTerm}`, {
      context: "Search Users",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Update user status
  async updateUserStatus(
    userId: string,
    status: string
  ): Promise<ApiResponse<User>> {
    const response = await enhancedApiClient.patch(`/users/${userId}/status`, {
      status,
    }, {
      context: "Update User Status",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Assign role to user
  async assignRole(
    userId: string,
    roleId: string
  ): Promise<ApiResponse<User>> {
    const response = await enhancedApiClient.patch(`/users/${userId}/role`, {
      roleId,
    }, {
      context: "Assign Role to User",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get user statistics
  async getUserStats(): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get("/users/stats", {
      context: "Get User Statistics",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get user permissions
  async getUserPermissions(userId: string): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get(`/users/${userId}/permissions`, {
      context: "Get User Permissions",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Update user password
  async updatePassword(
    userId: string,
    currentPassword: string,
    newPassword: string
  ): Promise<ApiResponse<{ message: string }>> {
    const response = await enhancedApiClient.patch(`/users/${userId}/password`, {
      currentPassword,
      newPassword,
    }, {
      context: "Update User Password",
      component: "UserService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }
}

export const userService = new UserService();
